/**
 * Sound effects synthesized with the Web Audio API — no audio files.
 * The AudioContext is created lazily on the first user gesture (browsers
 * block audio before that); failures degrade to silence. The mute choice
 * persists in localStorage.
 */

const MUTE_KEY = 'jungle.muted';

type ToneOptions = {
  freq: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
  slideTo?: number;
};

export class SoundPlayer {
  private ctx: AudioContext | null = null;
  private muted = false;

  constructor() {
    try {
      this.muted = window.localStorage.getItem(MUTE_KEY) === '1';
    } catch {
      this.muted = false; // storage unavailable (private mode) — default to sound on
    }
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    try {
      window.localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch {
      /* storage unavailable — keep the in-memory value */
    }
  }

  /** Call from any user-gesture handler so audio can start when allowed. */
  unlock(): void {
    if (this.muted) return;
    this.ensureContext();
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx === null) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return null;
      }
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume().catch(() => undefined);
    }
    return this.ctx;
  }

  private tone({ freq, duration, type = 'sine', gain = 0.12, delay = 0, slideTo }: ToneOptions): void {
    if (this.muted) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    try {
      const t0 = ctx.currentTime + delay;
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, t0);
      if (slideTo !== undefined) {
        osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + duration);
      }
      amp.gain.setValueAtTime(gain, t0);
      amp.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
      osc.connect(amp).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + duration + 0.02);
    } catch {
      /* never let audio break the game */
    }
  }

  select(): void {
    this.tone({ freq: 700, duration: 0.05, gain: 0.07 });
  }

  move(): void {
    this.tone({ freq: 340, duration: 0.08, type: 'triangle', gain: 0.12, slideTo: 240 });
  }

  capture(): void {
    this.tone({ freq: 160, duration: 0.16, type: 'square', gain: 0.14, slideTo: 70 });
    this.tone({ freq: 90, duration: 0.2, type: 'sawtooth', gain: 0.08, delay: 0.03, slideTo: 50 });
  }

  win(): void {
    [523, 659, 784, 1047].forEach((freq, i) => {
      this.tone({ freq, duration: 0.16, gain: 0.12, delay: i * 0.1 });
    });
  }

  loss(): void {
    [392, 330, 262].forEach((freq, i) => {
      this.tone({ freq, duration: 0.22, type: 'triangle', gain: 0.12, delay: i * 0.14 });
    });
  }

  draw(): void {
    this.tone({ freq: 440, duration: 0.14, gain: 0.1 });
    this.tone({ freq: 440, duration: 0.14, gain: 0.1, delay: 0.18 });
  }
}
